import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { MaterialGroup, MaterialGroupDataService } from '@lib/material-group';
import { BaseSiteLayoutModule } from '@lib/util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { PurchasingGroupDataService } from '../../services/purchasing-group-data.service';

/** A company code that is assigned to the purchasing group and can receive material-group assignments. */
export interface IBulkCompanyCode {
    readonly id: string;
    readonly code: string;
    readonly name: string | null;
}

export class PurchasingGroupBulkMaterialGroupsDialogData {
    constructor(
        public readonly purchasingGroupId: string,
        public readonly companyCodes: ReadonlyArray<IBulkCompanyCode>,
    ) {}
}

export class PurchasingGroupBulkMaterialGroupsDialogResult {
    constructor(public readonly updated: boolean) {}
}

/**
 * EPROC-11379: bulk-assign material groups to SEVERAL company codes of a purchasing group in one step.
 *
 * <p>The per-company-code dialog ({@link PurchasingGroupMaterialGroupsDialogComponent}) only targets a
 * single company code; this dialog adds a company-code multi-select so the user picks 1..n company
 * codes and 1..n material groups and the cross product is assigned via the existing
 * {@code POST /purchasing-groups/{id}/assignments/bulk/by-code} endpoint in a single call.
 */
@Component({
    selector: 'lib-purchasing-group-bulk-material-groups-dialog',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatDialogModule, BaseSiteLayoutModule, TranslateModule],
    templateUrl: './purchasing-group-bulk-material-groups-dialog.component.html',
    styleUrls: ['../material-groups/purchasing-group-material-groups-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasingGroupBulkMaterialGroupsDialogComponent implements OnInit {
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly materialGroupDataService = inject(MaterialGroupDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly translateService = inject(TranslateService);
    private readonly dialogRef = inject<
        MatDialogRef<PurchasingGroupBulkMaterialGroupsDialogComponent, PurchasingGroupBulkMaterialGroupsDialogResult>
    >(MatDialogRef);
    private readonly data = inject<PurchasingGroupBulkMaterialGroupsDialogData>(MAT_DIALOG_DATA);

    public readonly icon = 'fas fa-sitemap';
    // marker() lets the i18n extractor pick the heading up; the template translates it reactively so a
    // mid-session language switch updates the title.
    public readonly heading = marker('Warengruppen zuweisen');

    public readonly companyCodes: ReadonlyArray<IBulkCompanyCode> = [...this.data.companyCodes].sort((left, right) =>
        left.code.localeCompare(right.code, 'de', { sensitivity: 'base' }),
    );

    public readonly loading = signal(false);
    public readonly saving = signal(false);
    /**
     * Inline error rendered in the dialog body. The toast notification is forwarded to the parent shell
     * via postMessage inside the legacy iframe and may never surface there, so this banner always
     * renders in the dialog's own DOM and a save failure is never silent.
     */
    public readonly saveError = signal<string | null>(null);

    public readonly filterControl = new FormControl<string>('', { nonNullable: true });
    private readonly filterValue = toSignal(this.filterControl.valueChanges, { initialValue: '' });

    private readonly allMaterialGroups = signal<ReadonlyArray<MaterialGroup>>([]);

    public readonly selectedCompanyCodeIds = signal<ReadonlySet<string>>(new Set());
    /** Material groups are tracked by their business code — that is the key the bulk endpoint expects. */
    public readonly selectedMaterialGroupCodes = signal<ReadonlySet<string>>(new Set());

    public readonly filteredMaterialGroups = computed<ReadonlyArray<MaterialGroup>>(() => {
        const filter = this.filterValue().trim().toLowerCase();
        const groups = [...this.allMaterialGroups()].sort((left, right) =>
            left.code.localeCompare(right.code, 'de', { sensitivity: 'base' }),
        );
        if (!filter) return groups;
        return groups.filter(
            (mg) =>
                mg.code.toLowerCase().includes(filter) ||
                (mg.primaryName ?? '').toLowerCase().includes(filter),
        );
    });

    public readonly selectedCompanyCodeCount = computed(() => this.selectedCompanyCodeIds().size);
    public readonly selectedMaterialGroupCount = computed(() => this.selectedMaterialGroupCodes().size);
    public readonly assignmentCount = computed(
        () => this.selectedCompanyCodeCount() * this.selectedMaterialGroupCount(),
    );

    public readonly canSave = computed(
        () => !this.saving() && this.selectedCompanyCodeCount() > 0 && this.selectedMaterialGroupCount() > 0,
    );

    public readonly canSelectAllCompanyCodes = computed(
        () => !this.saving() && this.companyCodes.some((cc) => !this.selectedCompanyCodeIds().has(cc.id)),
    );
    public readonly canSelectAllVisibleMaterialGroups = computed(
        () =>
            !this.saving() &&
            this.filteredMaterialGroups().some((mg) => !this.selectedMaterialGroupCodes().has(mg.code)),
    );

    public ngOnInit(): void {
        this.loadMaterialGroups();
    }

    public companyCodeLabel(companyCode: IBulkCompanyCode): string {
        return companyCode.name ? `${companyCode.code} – ${companyCode.name}` : companyCode.code;
    }

    public isCompanyCodeSelected(id: string): boolean {
        return this.selectedCompanyCodeIds().has(id);
    }

    public isMaterialGroupSelected(code: string): boolean {
        return this.selectedMaterialGroupCodes().has(code);
    }

    public toggleCompanyCode(id: string): void {
        if (this.saving()) return;
        this.saveError.set(null);
        this.selectedCompanyCodeIds.update((current) => this.toggleInSet(current, id));
    }

    public toggleMaterialGroup(code: string): void {
        if (this.saving()) return;
        this.saveError.set(null);
        this.selectedMaterialGroupCodes.update((current) => this.toggleInSet(current, code));
    }

    public selectAllCompanyCodes(): void {
        if (!this.canSelectAllCompanyCodes()) return;
        this.saveError.set(null);
        this.selectedCompanyCodeIds.set(new Set(this.companyCodes.map((cc) => cc.id)));
    }

    public selectAllVisibleMaterialGroups(): void {
        if (!this.canSelectAllVisibleMaterialGroups()) return;
        this.saveError.set(null);
        this.selectedMaterialGroupCodes.update((current) => {
            const next = new Set(current);
            for (const mg of this.filteredMaterialGroups()) next.add(mg.code);
            return next;
        });
    }

    public async save(): Promise<void> {
        if (!this.canSave()) return;
        this.saveError.set(null);
        const companyCodeIds = [...this.selectedCompanyCodeIds()];
        const materialGroupCodes = [...this.selectedMaterialGroupCodes()];

        this.saving.set(true);
        try {
            await firstValueFrom(
                this.dataService.bulkAssignAssignments(this.data.purchasingGroupId, companyCodeIds, materialGroupCodes),
            );
            this.notificationService.showNotification(
                marker('Zuordnungen wurden gespeichert.'),
                NotificationMessageLevel.SUCCESS,
            );
            this.dialogRef.close(new PurchasingGroupBulkMaterialGroupsDialogResult(true));
        } catch (error) {
            this.handleSaveError(error);
        } finally {
            this.saving.set(false);
        }
    }

    public close(): void {
        this.dialogRef.close(new PurchasingGroupBulkMaterialGroupsDialogResult(false));
    }

    private toggleInSet(current: ReadonlySet<string>, value: string): ReadonlySet<string> {
        const next = new Set(current);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        return next;
    }

    private handleSaveError(error: unknown): void {
        if (this.dataService.isAssignmentConflict(error)) {
            const conflict = this.dataService.getAssignmentConflict(error);
            const message = conflict
                ? this.interpolate(
                      marker('Warengruppe {{wg}} ist bereits durch {{code}} – {{name}} belegt.'),
                      {
                          wg: conflict.materialGroupCode,
                          code: conflict.assignedPurchasingGroupCode,
                          name: conflict.assignedPurchasingGroupName,
                      },
                  )
                : this.translateService.instant(marker('Eine Zuordnung wurde zwischenzeitlich vergeben.'));
            this.saveError.set(message);
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            return;
        }
        const message = this.dataService.resolveErrorMessage(
            error,
            marker('Zuordnungen konnten nicht gespeichert werden.'),
        );
        this.saveError.set(message);
        this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
    }

    /**
     * Builds a parameter-substituted message. translateService.instant() only interpolates params for a
     * key already present in the loaded translation file; for a freshly added marker whose key has not
     * been extracted yet it returns the raw key with literal {{placeholders}}. Running the returned
     * template through the parser substitutes the params in every case.
     */
    private interpolate(key: string, params: Record<string, string>): string {
        const template = this.translateService.instant(key);
        return this.translateService.parser.interpolate(template, params) ?? template;
    }

    private loadMaterialGroups(): void {
        this.loading.set(true);
        this.materialGroupDataService.getAllMaterialGroups().subscribe({
            next: (materialGroups) => {
                this.allMaterialGroups.set([...materialGroups]);
                this.loading.set(false);
            },
            error: (error) => {
                this.loading.set(false);
                const message = this.dataService.resolveErrorMessage(
                    error,
                    marker('Warengruppen konnten nicht geladen werden.'),
                );
                this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            },
        });
    }
}
